import * as React from 'react'
import { FC, useCallback, useMemo } from 'react'
import { toast } from '@sales-center/components'
import { useSelector } from 'react-redux'
import * as yup from 'yup'

import { Formik } from 'formik'
import { ManageDefaultsFormFields } from './ManageDefaultsFormFields'
import { ManageDefaultSalesTeamsFormModel } from './typings'
import { selectAllSalesTeams } from '@user/store/salesTeam/slice'
import { useAppDispatch } from '@user/store/hooks/useAppDispatch'
import { updateDefaultSalesTeams } from '@user/store/salesTeam/api'
import { useGetSalesTeamListOnMount } from '@user/store/salesTeam/hooks'
import { useGetBranchListOnMount } from '@user/store/branches/hooks/useGetBranchListOnMount'

import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import CircularProgress from '@mui/material/CircularProgress'

interface Props {
  onClose: () => void
}

export const ManageDefaultsFormWrapper: FC<Props> = (props) => {
  const { onClose } = props
  const dispatch = useAppDispatch()

  // Hides company default sales team functionality
  const hideCompanyDefaultSalesTeam = true

  const salesTeamStatus = useGetSalesTeamListOnMount()
  const branchStatus = useGetBranchListOnMount()
  const salesTeams = useSelector(selectAllSalesTeams)

  const loading = salesTeamStatus == 'pending' || branchStatus == 'pending'
  const failed = salesTeamStatus == 'fail' || branchStatus == 'fail'

  const companyDefaultSalesTeamId = useMemo(() => {
    return salesTeams.find((team) => team.tenantDefault == true)?.id || -1
  }, [salesTeams])

  const branchDefaults = useMemo(() => {
    const defaults = {}
    salesTeams.forEach((team) => {
      if (
        !!defaults[`branch_${team.branchId}_salesTeam`] &&
        defaults[`branch_${team.branchId}_salesTeam`] != -1
      )
        return

      defaults[`branch_${team.branchId}_salesTeam`] =
        team.branchDefault == true ? team.id : -1
    })
    return defaults
  }, [salesTeams])

  const initialValues: ManageDefaultSalesTeamsFormModel = {
    companyDefaultSalesTeamId: companyDefaultSalesTeamId,
    branchDefaults: branchDefaults,
  }

  const validationSchema = yup.object({
    // companyDefaultSalesTeamId: yup
    //   .string()
    //   .required('Please select a Sales Team.'),
  })

  const onSubmit = useCallback(
    async (model: ManageDefaultSalesTeamsFormModel) => {
      const input = {
        companyDefault:
          hideCompanyDefaultSalesTeam || model.companyDefaultSalesTeamId == -1
            ? null
            : model.companyDefaultSalesTeamId,
        branchDefaults: Object.values(model.branchDefaults).filter(
          (value) => value != -1,
        ),
      }

      const response = await dispatch(updateDefaultSalesTeams(input))

      if (updateDefaultSalesTeams.fulfilled.match(response)) {
        toast.success('Successfully updated Sales Team defaults.')
        onClose()
      } else {
        toast.error('Failed to update Sales Team defaults.')
      }
    },
    [dispatch, hideCompanyDefaultSalesTeam, onClose],
  )

  return (
    <>
      {loading && (
        <Box p={2} display="flex" justifyContent="center">
          <CircularProgress />
        </Box>
      )}

      {!loading && (
        <Formik
          onSubmit={onSubmit}
          validationSchema={validationSchema}
          initialValues={initialValues}
        >
          <ManageDefaultsFormFields onCancel={onClose} />
        </Formik>
      )}

      {failed && (
        <Box p={2}>
          <Alert severity="error"> Failed to load Sales Team defaults. </Alert>
        </Box>
      )}
    </>
  )
}

ManageDefaultsFormWrapper.displayName = 'ManageDefaultsFormWrapper'
